import { useAppDispatch, useAppSelector } from '@store/hooks';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MdEdit } from 'react-icons/md';
import { FaRegTrashCan } from 'react-icons/fa6';
import { UPDATE_HOSPITAL_USER_PATH, UPDATE_USER_PATH } from '@constants/routes';
import { adminDeleteUser, getUserName } from '@utils/utils';
import EditIconWhite from '@components/SvgComponents/EditIconWhite';
import { FiEdit } from 'react-icons/fi';
import {
    setDeviceDetail,
    setTreeViewSelectedValue,
} from '@store/reducers/userSlice';
import ErrorMessage from '@components/Error/ErrorMessage';
import {
    addDeviceData,
    checkDeviceIdExists,
    checkHospitalDeviceIdExists,
    checkIndividualDeviceIdExists,
    deviceDetailUpdate,
    fetchHospitalBlockData,
} from '@utils/deviceUtils';
import { scan } from '@utils/authUtils';
import { DEVICE_TABLE, HOSPITALS_TABLE } from '@constants/dynamoDBTableName';
import { IoMdCloseCircle } from 'react-icons/io';
import { APPROVED, HOSPITAL } from '@constants/constants';
import Swal from 'sweetalert2';
import {
    DEVICE_NAME_MAX_LENGTH,
    DEVICE_NAME_MIN_LENGTH,
    DEVICE_NAME_TITLE,
    DEVICE_NUMBER_MAX_LENGTH,
    DEVICE_NUMBER_MIN_LENGTH,
    DEVICE_NUMBER_PATTERN,
    DEVICE_NUMBER_TITLE,
} from '@constants/form';
import {
    capitalizeFirstLetter,
    formatDateDMY,
    getDataUsingUserId,
} from '@utils/sharedUtils';
import MyTreeView from '@components/TreeView/TreeView';
import { useAuditLog } from '../../contexts/AuditLogContext';

const MySwal = Swal;

interface DeviceTableProps {
    data: any[];
    header: any[];
    totalData: any[];
    row: number;
    // currentpage:number;
}

interface DeviceData {
    id?: string;
    device_id?: string;
    device_name?: string;
    status?: string;
    created_at?: string;
    created_at_time?: string;
    created_by_name?: string;
    created_by?: string;
}

type TreeViewDataType = Record<string, any>[];

const DeviceManagementTable = ({
    data,
    header,
    totalData,
    row,
}: DeviceTableProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [updatedData, setData] = useState<any[]>([]);
    const [allData, setAllData] = useState(totalData);
    const [rowPerPage, setRowPerPage] = useState(row);
    const [error, setError] = useState<string | null>(null);
    const [treeViewData, setTreeViewData] = useState<TreeViewDataType>([]);
    const [showModal, setShowModal] = useState(false);
    const [nurseId, setNurseId] = useState('');
    const [deviceName, setDeviceName] = useState('');
    const [deviceStatus, setDeviceStatus] = useState('');
    const [deviceUniqueId, setDeviceUniqueId] = useState('');
    const [createdBy, setCreatedBy] = useState('');
    const [deviceTableId, setDeviceTableId] = useState('');
    const [initialDeviceTableId, setInitialDeviceTableId] = useState('');
    const loggedInUserInfoUuid = useAppSelector(
        (state) => state.login.loggedInUserInfoUuid
    );
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const role = useAppSelector((state) => state.login.loggedInUserRole);
    const treeViewSelectedValue = useAppSelector(
        (state) => state.user.treeViewSelectedValue
    );
    const loggedInUserID = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole
    );

    const { addLog } = useAuditLog();

    const fetchData = async () => {
        try {
            const hospitalBlock =
                await fetchHospitalBlockData(loggedInUserTenantID); // Await the promise
            setTreeViewData(hospitalBlock); // Set the fetched data to the state
        } catch (error) {
            console.error('Error fetching hospital block data:', error);
        }
    };

    useEffect(() => {
        setData(data); // Ensure this reflects the updated state correctly
        setAllData(totalData);
        setRowPerPage(row);
        if (loggedInUserRole.toLowerCase() == HOSPITAL.toLowerCase()) {
            fetchData();
        }
    }, [data, totalData, row, loggedInUserRole, HOSPITAL]);

    if (data.length === 0) {
        return (
            // <div className="card rounded-xl mt-5 p-4 mx-auto w-full">
            <div className="card rounded-xl mt-5 p-0 mx-auto w-full">
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="border-top">
                            <tr>
                                {header.map((device) => (
                                    <th
                                        className="font-semibold text-black text-base !normal-case"
                                        key={device.id}
                                    >
                                        {capitalizeFirstLetter(device.title)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="text-center">
                                <td colSpan={header.length + 1}>
                                    {t('user_management.user_table.no_data')}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Get the keys from the first object in the data array
    let keys = Object.keys(data[0]).filter(
        (key) =>
            key !== 'id' &&
            key !== 'created_at' &&
            key !== 'created_by_name' &&
            key !== 'hospital_block_id' &&
            key !== 'created_at_time' &&
            key !== 'created_by'
    );

    const handleRowClick = async (device: any) => {
        if (device && device.id) {
            // const createdBy = await getUserName(
            //     'user_info',
            //     device.created_by,
            //     'tenant_id'
            // );
            const createdBy = await getDataUsingUserId(
                'user_info',
                device.created_by
            );

            if (createdBy && createdBy.Items && createdBy.Items.length > 0) {
                const additionalDetails = createdBy.Items[0];
                const mergedUserDetails = {
                    device_uuid: device.id,
                    device_id: device.device_id,
                    device_status: device.device_status,
                    status: device.status,
                    room_no: '',
                    bed_no: '',
                    created_at: device.created_at,
                    created_by: additionalDetails?.name?.S
                        ? additionalDetails?.name?.S
                        : '',
                };
                dispatch(setDeviceDetail(mergedUserDetails));
                if (loggedInUserRole.toLowerCase() == HOSPITAL.toLowerCase()) {
                    navigate(`/management/device/${device.device_name}`);
                } else {
                    navigate(`/device/${device.device_name}`);
                }
            }
        } else {
            console.error('Device ID is missing');
            // Optionally, handle the error (e.g., show a notification to the user)
        }
    };

    const getStatusClass = (status: string) => {
        if (!status) {
            return ''; // Return an empty string if status is undefined or null
        }
        // console.log('Status:', status);
        switch (status.toLowerCase().trim()) {
            case 'approved':
                return 'text-white !text-sm bg-success inline-block rounded-2xl px-3 py-1';
            case 'pending':
                return 'text-white !text-sm bg-warning inline-block rounded-2xl px-3 py-1';
            case 'blocked':
                return 'text-white !text-sm bg-secondary inline-block rounded-2xl px-3 py-1';
            case 'non functional':
                return 'text-white !text-sm bg-danger inline-block rounded-2xl px-3 py-1';
            default:
                return '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            if (loggedInUserRole.toLowerCase() == HOSPITAL.toLowerCase()) {
                if (
                    initialDeviceTableId.toLowerCase() !=
                    deviceUniqueId.toLowerCase()
                ) {
                    const res = await checkHospitalDeviceIdExists(
                        deviceUniqueId.toLowerCase(),
                        loggedInUserTenantID
                    );
                    if (
                        res == 'Device ID exists' ||
                        res == 'Invalid Device ID'
                    ) {
                        setError(res);
                    } else {
                        await deviceDetailUpdate(
                            DEVICE_TABLE,
                            deviceTableId,
                            loggedInUserTenantID,
                            deviceName,
                            deviceUniqueId.toLowerCase(),
                            deviceStatus,
                            treeViewSelectedValue
                        );

                        addLog(
                            'Device Updated',
                            deviceTableId,
                            'Devices',
                            loggedInUserID,
                            { 'Device Name': deviceName, 'Device ID': deviceUniqueId, 'Status': deviceStatus },
                            window.location.hostname
                        );

                        const updated = updatedData.map((device) =>
                            device.id === deviceTableId
                                ? {
                                      ...device,
                                      device_name:
                                          capitalizeFirstLetter(deviceName),
                                      device_status:
                                          capitalizeFirstLetter('inactive'),
                                      status: capitalizeFirstLetter(
                                          deviceStatus
                                      ),
                                      device_id: deviceUniqueId,
                                  }
                                : device
                        );

                        setData(updated);

                        MySwal.fire({
                            position: 'top-end', // Position the toast in the top-right corner
                            icon: 'success',
                            title: 'Device updated successfully',
                            showConfirmButton: false,
                            timer: 1500,
                            toast: true,
                            customClass: {
                                popup: 'text-green bg-light_green', // Add custom class to the popup
                            },
                        });

                        // Reset form state
                        setDeviceName('');
                        setDeviceStatus('');
                        setDeviceUniqueId('');
                        setInitialDeviceTableId('');
                        setError(null);
                        setShowModal(false);
                    }
                } else {
                    await deviceDetailUpdate(
                        DEVICE_TABLE,
                        deviceTableId,
                        loggedInUserTenantID,
                        deviceName,
                        deviceUniqueId.toLowerCase(),
                        deviceStatus,
                        treeViewSelectedValue
                    );

                    const updated = updatedData.map((device) =>
                        device.id === deviceTableId
                            ? {
                                  ...device,
                                  device_name:
                                      capitalizeFirstLetter(deviceName),
                                  device_status:
                                      capitalizeFirstLetter('inactive'),
                                  status: capitalizeFirstLetter(deviceStatus),
                                  device_id: deviceUniqueId,
                              }
                            : device
                    );

                    setData(updated);

                    MySwal.fire({
                        position: 'top-end', // Position the toast in the top-right corner
                        icon: 'success',
                        title: 'Device updated successfully',
                        showConfirmButton: false,
                        timer: 1500,
                        toast: true,
                        customClass: {
                            popup: 'text-green bg-light_green', // Add custom class to the popup
                        },
                    });

                    // Reset form state
                    setDeviceName('');
                    setDeviceStatus('');
                    setDeviceUniqueId('');
                    setInitialDeviceTableId('');
                    setError(null);
                    setShowModal(false);
                }
            } else {
                if (
                    initialDeviceTableId.toLowerCase() !=
                    deviceUniqueId.toLowerCase()
                ) {
                    const res = await checkIndividualDeviceIdExists(
                        deviceUniqueId.toLowerCase(),
                        loggedInUserTenantID
                    );
                    if (
                        res == 'Device ID exists' ||
                        res == 'Invalid Device ID'
                    ) {
                        setError(res);
                    } else {
                        await deviceDetailUpdate(
                            DEVICE_TABLE,
                            deviceTableId,
                            loggedInUserTenantID,
                            deviceName,
                            deviceUniqueId.toLowerCase(),
                            deviceStatus,
                            treeViewSelectedValue
                        );

                        addLog(
                            'Device Updated',
                            deviceTableId,
                            'Devices',
                            loggedInUserID,
                            { 'Device Name': deviceName, 'Device ID': deviceUniqueId.toLowerCase(), 'Status': deviceStatus },
                            window.location.hostname
                        );

                        const updated = updatedData.map((device) =>
                            device.id === deviceTableId
                                ? {
                                      ...device,
                                      device_name:
                                          capitalizeFirstLetter(deviceName),
                                      device_status:
                                          capitalizeFirstLetter('inactive'),
                                      status: capitalizeFirstLetter(
                                          deviceStatus
                                      ),
                                      device_id: deviceUniqueId,
                                  }
                                : device
                        );

                        setData(updated);

                        MySwal.fire({
                            position: 'top-end', // Position the toast in the top-right corner
                            icon: 'success',
                            title: 'Device updated successfully',
                            showConfirmButton: false,
                            timer: 1500,
                            toast: true,
                            customClass: {
                                popup: 'text-green bg-light_green', // Add custom class to the popup
                            },
                        });

                        // Reset form state
                        setDeviceName('');
                        setDeviceStatus('');
                        setDeviceUniqueId('');
                        setInitialDeviceTableId('');
                        setError(null);
                        setShowModal(false);
                    }
                } else {
                    await deviceDetailUpdate(
                        DEVICE_TABLE,
                        deviceTableId,
                        loggedInUserTenantID,
                        deviceName,
                        deviceUniqueId.toLowerCase(),
                        deviceStatus,
                        treeViewSelectedValue
                    );

                    const updated = updatedData.map((device) =>
                        device.id === deviceTableId
                            ? {
                                  ...device,
                                  device_name:
                                      capitalizeFirstLetter(deviceName),
                                  device_status:
                                      capitalizeFirstLetter('inactive'),
                                  status: capitalizeFirstLetter(deviceStatus),
                                  device_id: deviceUniqueId,
                              }
                            : device
                    );

                    setData(updated);

                    MySwal.fire({
                        position: 'top-end', // Position the toast in the top-right corner
                        icon: 'success',
                        title: 'Device updated successfully',
                        showConfirmButton: false,
                        timer: 1500,
                        toast: true,
                        customClass: {
                            popup: 'text-green bg-light_green', // Add custom class to the popup
                        },
                    });

                    // Reset form state
                    setDeviceName('');
                    setDeviceStatus('');
                    setDeviceUniqueId('');
                    setInitialDeviceTableId('');
                    setError(null);
                    setShowModal(false);
                }
            }

            // Show success message
            // setSuccessMessage('Device added successfully!');
            // setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error: any) {
            console.error('Error checking device ID:', error);
            // throw new Error('Error checking device ID');
            let errorMessage;

            switch (error.name) {
                case 'ValidationException':
                    errorMessage =
                        'Validation error: Please check your request parameters.';
                    break;
                case 'ProvisionedThroughputExceededException':
                    errorMessage =
                        'Provisioned throughput exceeded: Please try again later.';
                    break;
                case 'ResourceNotFoundException':
                    errorMessage =
                        'Resource not found: Please check your table name.';
                    break;
                case 'InternalServerError':
                    errorMessage =
                        'Internal server error: Please try again later.';
                    break;
                case 'ServiceUnavailable':
                    errorMessage =
                        'Service unavailable: Please try again later.';
                    break;
                default:
                    errorMessage = `Unknown error: ${error.message || error}`;
            }
            setError(errorMessage);
            // return errorMessage;
        }
    };

    const handleEdit = async (deviceData: any) => {
        if (deviceData && deviceData.id) {
            dispatch(setTreeViewSelectedValue(deviceData.hospital_block_id));
            const createdBy = await getUserName(
                'user_info',
                deviceData.created_by,
                'user_id'
            );

            if (createdBy) {
                const additionalDetails = createdBy[0];
                setDeviceName(deviceData.device_name);
                setDeviceStatus(deviceData.status);
                setDeviceUniqueId(deviceData.device_id);
                setInitialDeviceTableId(deviceData.device_id);
                setDeviceTableId(deviceData.id);
                setCreatedBy(
                    additionalDetails?.name?.S
                        ? capitalizeFirstLetter(additionalDetails?.name?.S)
                        : ''
                );
                if (additionalDetails?.nurse_id?.S) {
                    setNurseId(
                        additionalDetails?.nurse_id?.S
                            ? additionalDetails?.nurse_id?.S
                            : ''
                    );
                }
                // setError(null);
                setShowModal(true);
            }
        }
    };

    const onCancel = () => {
        setShowModal(false);
    };
    // Calculate height based on number of rows and rows per page
    const tableHeight =
        allData.length >= 10 && rowPerPage === 10
            ? '36rem'
            : allData.length >= 5 && rowPerPage === 5
              ? '20rem'
              : 'auto';

    return (
        <>
            {/* <div className="card rounded-xl mt-5 p-4 mx-auto w-full"> */}
            <div
                className="card rounded-xl mt-5 p-0 mx-auto w-full"
                style={{ height: tableHeight }}
            >
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="m-3">
                            <tr>
                                {/* <th className=''></th> */}
                                {header.map((device) => (
                                    <th
                                        className="text-black text-base font-semibold py-3 !normal-case"
                                        key={device.id}
                                    >
                                        {capitalizeFirstLetter(device.title)}
                                    </th>
                                ))}
                                {/* <th className=''></th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {updatedData.map((device) => (
                                <tr key={device.id}>
                                    {/* <td></td> */}
                                    {keys.map((key) => {
                                        const value = device[key];
                                        // const isStatusKey =
                                        // key.toLowerCase().trim() ===
                                        //     'hospital_admin_status';
                                        return (
                                            <td
                                                key={key}
                                                onClick={() =>
                                                    handleRowClick(device)
                                                }
                                                className="cursor-pointer"
                                            >
                                                <div
                                                    className={`text-base font-normal text-table_header
                                                    ${getStatusClass(value)}`}
                                                    dangerouslySetInnerHTML={{
                                                        __html: value || 'N/A',
                                                    }}
                                                >
                                                    {/* {value ?? 'N/A'} */}
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td>
                                        <div className="flex space-x-5 items-center">
                                            <div
                                                className="cursor-pointer"
                                                // onClick={handleUpdateUser}
                                            >
                                                <button
                                                    className="btn text-white bg-primary py-2 px-3"
                                                    onClick={() =>
                                                        handleEdit(device)
                                                    }
                                                >
                                                    {/* <FiEdit className='text-white top-1.5 left-1 absolute cursor-pointer' /> */}
                                                    <FiEdit className="text-white cursor-pointer mr-2" />
                                                    {t(
                                                        'management.device.edit'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                    {/* <td className=''></td> */}
                                </tr>
                            ))}
                            <tr></tr>
                        </tbody>
                    </table>
                </div>
            </div>
            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-75 z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg relative w-1/3">
                        <div className="text-center !text-black text-lg font-semibold">
                            Update Device
                        </div>
                        <button
                            className="absolute top-0 right-0 mt-2 mr-2 !text-black"
                            onClick={onCancel}
                            aria-label="close"
                        >
                            <IoMdCloseCircle className="!text-lg" />
                        </button>
                        <div className="my-4">
                            {error && (
                                <div className="w-3/5 mx-auto">
                                    <ErrorMessage message={error} />
                                </div>
                            )}
                            <form
                                className="w-4/5 mx-auto mt-3"
                                onSubmit={handleSubmit}
                            >
                                <div className="mx-auto">
                                    <label
                                        htmlFor="device_unique_id"
                                        className="mb-2 text-sm font-medium text-light_grey"
                                    >
                                        {t(
                                            'management.device.device_unique_id'
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        name="device_unique_id"
                                        id="device_unique_id"
                                        className="form-control px-3 py-2 placeholder-gains_boro"
                                        placeholder={t(
                                            'management.device.placeholder'
                                        )}
                                        value={deviceUniqueId}
                                        onChange={(e) => {
                                            if (/^\d*$/.test(e.target.value)) {
                                                setDeviceUniqueId(
                                                    e.target.value
                                                );
                                            }
                                        }}
                                        pattern={DEVICE_NUMBER_PATTERN}
                                        title={DEVICE_NUMBER_TITLE}
                                        minLength={DEVICE_NUMBER_MIN_LENGTH}
                                        maxLength={DEVICE_NUMBER_MAX_LENGTH}
                                    />
                                </div>
                                <div className="mx-auto mt-4">
                                    <label
                                        htmlFor="device_name"
                                        className="mb-2 text-sm font-medium text-light_grey"
                                    >
                                        {t(
                                            'management.device.device_name'
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        name="device_name"
                                        id="device_name"
                                        className="form-control px-3 py-2 placeholder-gains_boro"
                                        placeholder="Enter device name"
                                        value={deviceName}
                                        onChange={(e) =>
                                            setDeviceName(e.target.value)
                                        }
                                        minLength={DEVICE_NAME_MIN_LENGTH}
                                        maxLength={DEVICE_NAME_MAX_LENGTH}
                                        title={DEVICE_NAME_TITLE}
                                    />
                                </div>
                                <div className="mx-auto mt-4">
                                    <label
                                        htmlFor="device_status"
                                        className="mb-2 text-sm font-medium text-light_grey"
                                    >
                                        {t(
                                            'management.device.status'
                                        )}
                                    </label>
                                    <select
                                        name="device_status"
                                        id="device_status"
                                        className="w-full px-3 py-[0.65rem] border rounded !placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                                        value={deviceStatus.toLowerCase()}
                                        onChange={(e) =>
                                            setDeviceStatus(e.target.value)
                                        }
                                    >
                                        <option value="approved">
                                        {t(
                                            'management.device.approve'
                                        )}
                                        </option>
                                        <option value="pending">{t(
                                            'management.device.pending'
                                        )}</option>
                                        <option value="blocked">{t(
                                            'management.device.block'
                                        )}</option>
                                        <option value="non functional">
                                        {t(
                                            'management.device.non_functional'
                                        )}
                                        </option>
                                    </select>
                                </div>
                                {loggedInUserRole.toLowerCase() ==
                                    HOSPITAL.toLowerCase() && (
                                <div className="mx-auto mt-4">
                                    <label
                                        htmlFor="created_by"
                                        className="mb-2 text-sm font-medium text-light_grey"
                                    >
                                       {t(
                                            'management.device.added_by'
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        name="created_by"
                                        id="created_by"
                                        className="form-control px-3 py-2 placeholder-gains_boro"
                                        placeholder="Enter device name"
                                        readOnly
                                        disabled
                                        value={createdBy}
                                    />
                                </div>
                                    )}
                                {nurseId && (
                                    <div className="mx-auto mt-4">
                                        <label
                                            htmlFor="created_by"
                                            className="mb-2 text-sm font-medium text-light_grey"
                                        >
                                        {t(
                                            'management.device.nurse_id'
                                        )}
                                        </label>
                                        <input
                                            type="text"
                                            name="created_by"
                                            id="created_by"
                                            className="form-control px-3 py-2 placeholder-gains_boro"
                                            placeholder="Enter device name"
                                            readOnly
                                            disabled
                                            value={nurseId}
                                        />
                                    </div>
                                )}
                                {loggedInUserRole.toLowerCase() ==
                                    HOSPITAL.toLowerCase() && (
                                    <div className="mx-auto mt-4">
                                        <label
                                            htmlFor="created_by"
                                            className="mb-2 text-sm font-medium text-light_grey"
                                        >
                                            {t('tree_view.title')}
                                        </label>
                                        <div className="tree-container_1">
                                            <div className="checkbox-container">
                                                <MyTreeView
                                                    data={treeViewData}
                                                    isDisabled={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="flex cursor-pointer align-items-center justify-content-center mx-auto mt-4">
                                    <button
                                        className="bg-primary text-lg text-white py-2  px-4 rounded-lg w-full"
                                        type="submit"
                                    >
                                        {t(
                                            'management.device.submit'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default DeviceManagementTable;
